import { parseDeclarationStatus } from './statusModel'
import type { WeatherSnapshot } from '../types/weather'
import type { DeclarationStatus } from './statusModel'
import type {
  AsyncState,
  FlightDeclaration,
  Geofence,
  MavCommand,
  MavlinkTelemetry,
  TrafficObservation,
  TrustSnapshot,
  IncidentsSnapshot, TrajSnapshot, TwinOverlay,
  } from '../types'

/* ============================================================================
 * КЛИЕНТ КОНСОЛЬНОМУ API
 *
 * Бэкенд — консольный сервер UTM на stdlib Python.
 * ВАЖНО: /api/mavlink и /api/declarations могут «зависать», если ядро
 * (Flight Blender + Keycloak) выключено. Поэтому каждый запрос оборачивается
 * в AbortController с таймаутом, а UI показывает деградацию вместо зависания.
 * ========================================================================= */

/** Таймауты, мс */
export const TIMEOUT = {
  /** /api/trust отвечает быстро */
  trust: 4000,
  /** Телеметрия MAVLink может блокироваться без подключённого БВС */
  mavlink: 2500,
  /** Декларации требуют работающего FB+Keycloak */
  declarations: 4000,
  geo: 4000,
  /** Метео: консоль -> адаптер -> aviationweather, внешний хоп */
  weather: 8000,
  traffic: 6000,
  action: 6000,
} as const

/** Внутренняя ошибка запроса с кодом */
export class ApiError extends Error {
  readonly reason: 'timeout' | 'network' | 'backend'
  readonly status?: number

  constructor(message: string, reason: 'timeout' | 'network' | 'backend', status?: number) {
    super(message)
    this.name = 'ApiError'
    this.reason = reason
    this.status = status
  }
}

/** Выполняет fetch с жёстким таймаутом через AbortController */
export async function request<T>(path: string, init: RequestInit, timeoutMs: number): Promise<T> {
  const controller = new AbortController()
  const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs)
  try {
    const res = await fetch(path, { ...init, signal: controller.signal })
    if (!res.ok) {
      throw new ApiError(`HTTP ${res.status} ${res.statusText}`, 'backend', res.status)
    }
    return (await res.json()) as T
  } catch (err) {
    if (err instanceof ApiError) throw err
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(`Превышен таймаут ${timeoutMs} мс: ${path}`, 'timeout')
    }
    throw new ApiError(`Сеть недоступна: ${path}`, 'network')
  } finally {
    globalThis.clearTimeout(timer)
  }
}

export const json = (body: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

/* ------------------------------------------------------- РАЗВОРАЧИВАНИЕ ОТВЕТОВ */
/**
 * КРИТИЧНО (задача №12, найдено 2026-10-04 по стектрейсу падения).
 * Консоль НЕ отдаёт «голые» массивы — почти все маршруты FB оборачивают
 * список в объект-конверт:
 *   /api/declarations -> { ok, count, results: [...] }
 *   /api/geo          -> { ok, count, results: [...] }
 *   /api/traffic      -> { observations: [...], count, cached }
 *
 * Фронтенд при этом объявлял типы как FlightDeclaration[]/Geofence[]/
 * TrafficObservation[] и скармливал объект прямо в .map()/.filter():
 *   TypeError: r.map is not a function
 * То есть ТИПЫ проходили компиляцию, а на живых данных приложение падало
 * в белый экран сразу после успешного входа. Такое расхождение «тип ↔ факт»
 * ловится только реальным запросом к бэкенду, а не чтением аннотаций.
 *
 * Здесь разворачиваем конверт ОДИН раз на границе API, чтобы все потребители
 * получали настоящий массив и никто не знал про обёртку. Функция терпима к
 * трём видам ответа (конверт / массив / null) — это защита от смены формата
 * бэкенда, а не только от текущего вида.
 */
type ListEnvelope<T> =
  | { results?: T[] }
  | { observations?: T[] }
  | T[]
  | null
  | undefined

/** Достать массив из конверта, не падая на смене формата бэкенда */
function unwrap<T>(data: unknown, key: 'results' | 'observations'): T[] {
  if (Array.isArray(data)) return data as T[]
  if (data && typeof data === 'object') {
    const env = data as Record<string, unknown>
    const inner = env[key]
    if (Array.isArray(inner)) return inner as T[]
  }
  return []
}

/* ------------------------------------------------------------------ /api/weather */
/**
 * Метео аэродрома (задача №10). Консоль ходит к адаптеру METAR напрямую, минуя
 * FB: FB отдаёт формат Open-Meteo и теряет категорию полёта, видимость и время
 * наблюдения — для диспетчера это ровно те поля, которые нужны.
 *
 * Таймаут здесь больше обычного: цепочка консоль → адаптер → aviationweather
 * включает внешний HTTP-запрос, и 4 с на два хопа — слишком туго.
 */
export function fetchWeather(signal?: AbortSignal): Promise<WeatherSnapshot> {
  return request<WeatherSnapshot>('/api/weather', { signal }, TIMEOUT.weather)
}

/* ---------------------------------------------------------- /api/incidents */
export function fetchIncidents(signal?: AbortSignal): Promise<IncidentsSnapshot> {
  return request<IncidentsSnapshot>('/api/incidents', { signal }, TIMEOUT.trust)
}

/* ---------------------------------------------------------------- /api/trust */
export function fetchTrust(signal?: AbortSignal): Promise<TrustSnapshot> {
  return request<TrustSnapshot>('/api/trust', { signal }, TIMEOUT.trust)
}

/* ------------------------------------------------------------- /api/mavlink */
export function fetchMavlink(signal?: AbortSignal): Promise<MavlinkTelemetry> {
  return request<MavlinkTelemetry>('/api/mavlink', { signal }, TIMEOUT.mavlink)
}

/** Команда управления БВС: ARM/DISARM/TAKEOFF/RTL/HOLD */
export function sendMavCommand(cmd: MavCommand, target = 1, alt = 0): Promise<unknown> {
  return request<unknown>('/api/mavcmd', json({ cmd, target, alt }), TIMEOUT.action)
}

/* ---------------------------------------------------------- /api/declarations */
/** Консоль отдаёт { ok, count, results: [...] } — разворачиваем в массив */
export function fetchDeclarations(signal?: AbortSignal): Promise<FlightDeclaration[]> {
  return request<ListEnvelope<FlightDeclaration>>('/api/declarations', { signal }, TIMEOUT.declarations)
    .then((d) => unwrap<FlightDeclaration>(d, 'results'))
}

/** Согласование / отклонение декларации.
 *  §6.2 (2026-10-07): при конфликте района с запретной зоной сервер
 *  отвечает 409 + { ok:false, conflict:{ zones, hits, message } }.
 *  Разбираем тело 409 и бросаем DeclarationConflictError с зонами —
 *  вызывающий код предлагает «Одобрить всё равно» (force=true). */
export class DeclarationConflictError extends Error {
  readonly zones: string[]
  constructor(payload?: { conflict?: { zones?: string[]; message?: string }; error?: string } | null) {
    super(payload?.conflict?.message || payload?.error || 'Район заявки пересекает запретные зоны')
    this.name = 'DeclarationConflictError'
    this.zones = payload?.conflict?.zones ?? []
  }
}

/** Тело 409 нужно ДО возбуждения ApiError — request() его отбрасывает,
 *  поэтому здесь разовый fetch-обёртка с чтением json ошибки. */
export function reviewDeclaration(
  id: string,
  isApproved: boolean,
  approvedBy: string,
  force = false,
): Promise<unknown> {
  const controller = new AbortController()
  const timer = globalThis.setTimeout(() => controller.abort(), TIMEOUT.action)
  return fetch(`/api/declarations/${encodeURIComponent(id)}/review`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_approved: isApproved, approved_by: approvedBy, force }),
    signal: controller.signal,
  })
    .then(async (res) => {
      if (res.ok) return res.json()
      if (res.status === 409) {
        let payload: { conflict?: { zones?: string[]; message?: string }; error?: string } | null = null
        try {
          payload = (await res.json()) as typeof payload
        } catch {
          payload = null
        }
        throw new DeclarationConflictError(payload)
      }
      throw new ApiError(`HTTP ${res.status} ${res.statusText}`, 'backend', res.status)
    })
    .finally(() => globalThis.clearTimeout(timer))
}

/* ------------------------------------------------------------------ /api/geo */
/** Консоль отдаёт { ok, count, results: [...] } — разворачиваем в массив */
export function fetchGeofences(signal?: AbortSignal): Promise<Geofence[]> {
  return request<ListEnvelope<Geofence>>('/api/geo', { signal }, TIMEOUT.geo)
    .then((d) => unwrap<Geofence>(d, 'results'))
}

export function createGeofence(fence: Geofence): Promise<unknown> {
  return request<unknown>('/api/geo', json(fence), TIMEOUT.action)
}

export function deleteGeofence(id: string): Promise<unknown> {
  return request<unknown>(`/api/geo/${encodeURIComponent(id)}`, { method: 'DELETE' }, TIMEOUT.action)
}

/* ---------------------------------------------------- /api/trajectories */
/** Хвосты траекторий + прогноз движения (10/30/60/300 с) + конфликты. */
export function fetchTrajectories(signal?: AbortSignal): Promise<TrajSnapshot> {
  return request<TrajSnapshot>('/api/trajectories', { signal }, TIMEOUT.traffic)
}

/* ------------------------------------------------- /api/l3/overlay (D3) */
/**
 * Оверлей цифрового двойника: «двойник vs реальность» в контракте
 * /api/trajectories (TrajSnapshot) + секция twin_overlay (метрики/доверие).
 * Это прокси консоли → L3-оркестратор (API /api/overlay twin-overlay-адаптера).
 * Эндпоинт опционален: если консоль ещё не отдаёт /api/l3/overlay (гресте
 * рестарта), фронт грациозно показывает «нет данных» и НЕ ломает карту.
 */
export function fetchTwinOverlay(signal?: AbortSignal): Promise<TwinOverlay> {
  return request<TwinOverlay>('/api/l3/overlay', { signal }, TIMEOUT.traffic)
}

/* --------------------------------------------------------------- /api/traffic */
/**
 * Запрос воздушного трафика по ограничивающему прямоугольнику.
 * ВНИМАНИЕ: ключ единственный — `bbox`, формат "lat1,lon1,lat2,lon2".
 * Отдельных параметров lat1/lon1 НЕТ.
 *
 * source (2026-10-07i): 'prod' (по умолчанию) — прод-FB; 'l3' — SIM-обстановка
 * twin-сессии L3-тренажёра (консоль проксирует оркестратору; прод-zg twin-трафик
 * не видит — изоляция «сценарии только на twin»).
 */
export function fetchTraffic(
  bbox: { lat1: number; lon1: number; lat2: number; lon2: number },
  signal?: AbortSignal,
  source: 'prod' | 'l3' = 'prod',
): Promise<TrafficObservation[]> {
  const bboxStr = [bbox.lat1, bbox.lon1, bbox.lat2, bbox.lon2].join(',')
  const srcQ = source === 'l3' ? '&source=l3' : ''
  // Консоль отдаёт { observations: [...], count, cached } — разворачиваем
  return request<ListEnvelope<TrafficObservation>>(`/api/traffic?bbox=${bboxStr}${srcQ}`, { signal }, TIMEOUT.traffic)
    .then((d) => unwrap<TrafficObservation>(d, 'observations'))
}

/* ------------------------------------------------------- нормализация статуса */
/**
 * Привести декларацию к каноническому статусу (задача №7).
 *
 * Бэкенд отдаёт либо `status` (строка), либо FB-овские `state` / `is_approved`.
 * Здесь это сводится к ОДНОМУ закрытому набору, причём нераспознанное значение
 * возвращается в `raw` и показывается оператору, а не заменяется на «готово».
 */
export function normalizeDeclarationStatus(d: FlightDeclaration): {
  status: DeclarationStatus
  raw?: string
} {
  return parseDeclarationStatus(d.status ?? d.state, {
    isApproved: d.is_approved,
  })
}

/* ==================================================================
 * Обёртка для useAsyncResource: превращает исключение в AsyncState
 * ================================================================== */
export async function resolveAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  previous: AsyncState<T> | null = null,
): Promise<AsyncState<T>> {
  const controller = new AbortController()
  try {
    const data = await loader(controller.signal)
    return { status: 'ok', data, error: null, updatedAt: new Date().toISOString(), degradedReason: null }
  } catch (err) {
    // Показываем последние хорошие данные вместо пустоты
    const reason = err instanceof ApiError ? err.reason : 'backend'
    const message = err instanceof Error ? err.message : String(err)
    return {
      status: reason === 'timeout' ? 'degraded' : 'error',
      data: previous?.data ?? null,
      error: message,
      updatedAt: previous?.updatedAt ?? null,
      degradedReason: reason,
    }
  }
}

/* ============================================================================
 * L3 ТРЕНАЖЁР №1 «UTM-диспетчер на twin» (2026-10-07i)
 * Оркестратор сессии — отдельный процесс (:8106, только localhost на хосте),
 * консоль ИВО проксирует /api/l3/*. Ручки — только ЦУП (ivo_auth.RULES).
 * ========================================================================= */

/** Статус L3-сессии (прокси консоли -> l3_orchestrator /api/status) */
export interface L3Status {
  running: boolean
  pid?: number
  session_id?: string | null
  scenario?: number | null
  uptime_s?: number
  inject?: { ticks: number; obs: number; errors: number; last_status: unknown; t: number }
  bridge?: { paused: boolean; ac: number; injected: number; errors: number; last_error: string | null; polls: number; ok: number }
  zg?: { ticks: number; errors: number; decls: number }
  trainer?: {
    up: boolean
    ai_active?: boolean | null
    ai_last_action?: number[] | null
    nearest_nm?: number | null
    intrusions?: number | null
    n_ac?: number
    last_error?: string | null
  }
  events?: Array<{ kind: string; drone: string; ts: string; text: string }>
  twin?: Record<string, unknown>
  error?: string
}

/** GET /api/l3/status — прокси консоли к оркестратору. Недоступен -> running:false */
export async function fetchL3Status(signal?: AbortSignal): Promise<L3Status> {
  return request<L3Status>('/api/l3/status', { signal }, TIMEOUT.action)
}

/** POST /api/l3/start|stop|ai — оркестрация сессии (только ЦУП) */
export async function l3Action(
  action: 'start' | 'stop' | 'ai',
  scenario?: 1 | 2,
): Promise<{ ok: boolean; error?: string; pid?: number; stopped?: string[] | null; auto?: boolean }> {
  return request(
    `/api/l3/${action}`,
    json(action === 'start' ? { scenario: scenario ?? 2 } : {}),
    TIMEOUT.action,
  )
}

/* ---------------------------------------------------------------------------
 * Журнал оценки (2026-10-07j): GET /api/l3/eval — прокси консоли к
 * оркестратору. Действия стажёра/инструктора + периоды AI-эталона с
 * метриками + scorecard. Читают l3_trainee и l3_instructor (RULES).
 * ------------------------------------------------------------------------ */
export interface L3Eval {
  ok: boolean
  session?: {
    session_id?: string | null
    scenario?: number | null
    started?: string | null
    ai_active?: boolean
  }
  trainee_actions?: Array<{
    ts: string
    user: string
    roles: string[]
    action: string
    detail: Record<string, unknown>
  }>
  ai_periods?: Array<{
    on: boolean
    from_ts: string
    to_ts: string
    dur_s: number
    d_intrusions: number | null
    mean_nearest_nm: number | null
    n_samples: number
  }>
  ai_open_period?: { on: boolean; from_ts: string; dur_s: number; n_samples: number } | null
  twin_events?: Array<{ kind: string; drone: string; ts: string; text: string }>
  incidents?: {
    ok?: boolean
    incidents?: Array<Record<string, unknown>>
    total_all?: number
    by_kind?: Record<string, number>
    error?: string
  }
  scorecard?: {
    incidents_total: number
    incidents_by_kind: Record<string, number>
    trainee_cmds: number
    trainee_actions_total: number
    zone_intrusion_reaction_s: { n: number; mean: number | null; list: number[] }
    ai_on_total_s: number
    ai_periods_count: number
  }
  semantics?: Record<string, string>
  error?: string
}

/** GET /api/l3/eval — журнал оценки (прокси консоли -> оркестратор :8106). */
export async function fetchL3Eval(signal?: AbortSignal): Promise<L3Eval> {
  return request<L3Eval>('/api/l3/eval', { signal }, TIMEOUT.action)
}
